import React, { useState, useRef } from 'react';
import {
  Settings, Palette, Upload, Trash2, RotateCcw, Check, Image, Sun,
  Eye, ChevronDown, ChevronUp, School,
} from 'lucide-react';
import { useTheme, PRESET_THEMES } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import '../styles/settings.css';

const COLOR_FIELDS = [
  { key: 'primary', label: 'Primary Color', desc: 'Main brand color — buttons, links, headers' },
  { key: 'primaryDark', label: 'Primary Dark', desc: 'Darker shade — header bars, hover states' },
  { key: 'primaryLight', label: 'Primary Light', desc: 'Lighter shade — gradients, highlights' },
  { key: 'secondary', label: 'Secondary Color', desc: 'Accent color — badges, tags' },
  { key: 'accent', label: 'Accent Color', desc: 'Highlight color — notifications, alerts' },
  { key: 'accentLight', label: 'Accent Light', desc: 'Subtle accent backgrounds' },
  { key: 'bodyBg', label: 'Page Background', desc: 'Main page background color' },
  { key: 'sidebarBg', label: 'Sidebar Background', desc: 'Sidebar panel color' },
  { key: 'sidebarHover', label: 'Sidebar Hover', desc: 'Sidebar item hover state' },
  { key: 'cardBg', label: 'Card Background', desc: 'Cards and panels' },
  { key: 'textPrimary', label: 'Text Primary', desc: 'Headings and body text' },
  { key: 'textSecondary', label: 'Text Secondary', desc: 'Descriptions, secondary info' },
  { key: 'textMuted', label: 'Text Muted', desc: 'Placeholders, subtle labels' },
  { key: 'border', label: 'Border Color', desc: 'Cards, inputs, dividers' },
];

const SettingsPage = () => {
  const { themeColors, activePreset, schoolLogo, logoFileName, applyPreset, updateColor, updateLogo, removeLogo, resetToDefault } = useTheme();
  const { school } = useAuth();
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');
  const fileInputRef = useRef(null);

  const showSaved = (msg) => {
    setSavedMsg(msg);
    setTimeout(() => setSavedMsg(''), 2500);
  };

  const handlePresetClick = (presetId) => {
    applyPreset(presetId);
    showSaved('Theme applied!');
  };

  const handleColorChange = (key, value) => {
    updateColor(key, value);
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      alert('Logo must be under 2MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      updateLogo(reader.result, file.name);
      showSaved('Logo uploaded!');
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    removeLogo();
    if (fileInputRef.current) fileInputRef.current.value = '';
    showSaved('Logo removed');
  };

  const handleReset = () => {
    if (window.confirm('Reset to default theme? This will remove your custom colors and logo.')) {
      resetToDefault();
      if (fileInputRef.current) fileInputRef.current.value = '';
      showSaved('Reset to default!');
    }
  };

  return (
    <div className="settings-page">
      {savedMsg && (
        <div className="settings-toast">
          <Check size={16} /> {savedMsg}
        </div>
      )}

      {/* SCHOOL BRANDING SECTION */}
      <div className="settings-section">
        <div className="section-header">
          <div className="section-icon"><School size={20} /></div>
          <div>
            <h2>School Branding</h2>
            <p>Upload your school logo — it will appear on all generated documents (Report Cards, ID Cards, Certificates).</p>
          </div>
        </div>

        <div className="logo-section">
          <div className="logo-preview">
            {schoolLogo ? (
              <img src={schoolLogo} alt="School Logo" className="logo-image" />
            ) : (
              <div className="logo-placeholder">
                <Image size={32} />
                <span>No logo uploaded</span>
              </div>
            )}
          </div>
          <div className="logo-controls">
            <input
              type="file"
              ref={fileInputRef}
              accept="image/png,image/jpeg,image/svg+xml,image/webp"
              onChange={handleLogoUpload}
              style={{ display: 'none' }}
              id="logo-upload"
            />
            <button className="btn btn-primary" onClick={() => fileInputRef.current?.click()}>
              <Upload size={16} /> Upload Logo
            </button>
            {schoolLogo && (
              <button className="btn btn-danger btn-sm" onClick={handleRemoveLogo}>
                <Trash2 size={14} /> Remove
              </button>
            )}
            {logoFileName && <span className="logo-filename">{logoFileName}</span>}
            <p className="logo-hint">PNG, JPG, SVG or WebP. Max 2MB. Recommended: 200x200px or larger, square aspect ratio.</p>
          </div>
        </div>
      </div>

      {/* THEME PRESETS SECTION */}
      <div className="settings-section">
        <div className="section-header">
          <div className="section-icon"><Palette size={20} /></div>
          <div>
            <h2>Color Theme</h2>
            <p>Choose a preset theme or customize individual colors. Theme applies to your dashboard and all generated documents.</p>
          </div>
        </div>

        <div className="presets-grid">
          {PRESET_THEMES.map((preset) => (
            <button
              key={preset.id}
              className={`preset-card ${activePreset === preset.id ? 'active' : ''}`}
              onClick={() => handlePresetClick(preset.id)}
            >
              <div className="preset-colors">
                <span className="preset-swatch" style={{ background: preset.colors.primary }} />
                <span className="preset-swatch" style={{ background: preset.colors.primaryDark }} />
                <span className="preset-swatch" style={{ background: preset.colors.secondary }} />
                <span className="preset-swatch" style={{ background: preset.colors.bodyBg, border: '1px solid #e2e8f0' }} />
              </div>
              <div className="preset-info">
                <strong>{preset.name}</strong>
                <span>{preset.description}</span>
              </div>
              {activePreset === preset.id && (
                <div className="preset-active-badge"><Check size={14} /> Active</div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ADVANCED COLOR PICKERS */}
      <div className="settings-section">
        <button className="section-toggle" onClick={() => setShowAdvanced(!showAdvanced)}>
          <div className="section-header" style={{ cursor: 'pointer' }}>
            <div className="section-icon"><Sun size={20} /></div>
            <div>
              <h2>Advanced Color Customization</h2>
              <p>Fine-tune every color in your theme with precision color pickers.</p>
            </div>
          </div>
          {showAdvanced ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>

        {showAdvanced && (
          <div className="color-grid">
            {COLOR_FIELDS.map(({ key, label, desc }) => (
              <div className="color-field" key={key}>
                <div className="color-label-row">
                  <label>{label}</label>
                  <span className="color-hex">{themeColors[key]}</span>
                </div>
                <p className="color-desc">{desc}</p>
                <div className="color-input-row">
                  <input
                    type="color"
                    value={themeColors[key]}
                    onChange={(e) => handleColorChange(key, e.target.value)}
                    className="color-picker"
                  />
                  <input
                    type="text"
                    value={themeColors[key]}
                    onChange={(e) => {
                      if (/^#[0-9a-fA-F]{6}$/.test(e.target.value)) handleColorChange(key, e.target.value);
                    }}
                    className="color-text-input"
                    placeholder="#000000"
                    maxLength={7}
                  />
                  <div className="color-preview-bar" style={{ background: themeColors[key] }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* LIVE PREVIEW */}
      <div className="settings-section">
        <div className="section-header">
          <div className="section-icon"><Eye size={20} /></div>
          <div>
            <h2>Live Preview</h2>
            <p>See how your theme looks on common UI elements.</p>
          </div>
        </div>

        <div className="theme-preview-box">
          <div className="preview-header" style={{ background: `linear-gradient(135deg, ${themeColors.primary}, ${themeColors.primaryLight})` }}>
            <div className="preview-header-content">
              {schoolLogo && <img src={schoolLogo} alt="" className="preview-logo" />}
              <div>
                <h3 style={{ color: '#fff', margin: 0 }}>{school?.name || 'Your School Name'}</h3>
                <p style={{ color: 'rgba(255,255,255,0.85)', margin: 0, fontSize: '12px' }}>School Management Dashboard</p>
              </div>
            </div>
          </div>

          <div className="preview-body" style={{ background: themeColors.bodyBg }}>
            <div className="preview-sidebar" style={{ background: themeColors.sidebarBg, borderRight: `1px solid ${themeColors.border}` }}>
              <div className="preview-nav-item active" style={{ background: `linear-gradient(135deg, ${themeColors.primary}, ${themeColors.primaryLight})`, color: '#fff' }}>Dashboard</div>
              <div className="preview-nav-item" style={{ color: themeColors.textSecondary }}>Students</div>
              <div className="preview-nav-item hover" style={{ background: themeColors.sidebarHover, color: themeColors.textPrimary }}>Teachers</div>
              <div className="preview-nav-item" style={{ color: themeColors.textSecondary }}>Reports</div>
            </div>
            <div className="preview-main">
              <div className="preview-card" style={{ background: themeColors.cardBg, border: `1px solid ${themeColors.border}` }}>
                <h4 style={{ color: themeColors.textPrimary, margin: '0 0 8px' }}>Student Overview</h4>
                <p style={{ color: themeColors.textSecondary, fontSize: '13px', margin: '0 0 12px' }}>Total enrollment this year</p>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <span className="preview-badge" style={{ background: `${themeColors.primary}20`, color: themeColors.primary }}>1,245 Students</span>
                  <span className="preview-badge" style={{ background: `${themeColors.secondary}20`, color: themeColors.secondary }}>98% Active</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                <button className="preview-btn" style={{ background: `linear-gradient(135deg, ${themeColors.primary}, ${themeColors.primaryLight})`, color: '#fff' }}>Primary Button</button>
                <button className="preview-btn outline" style={{ border: `1px solid ${themeColors.primary}`, color: themeColors.primary, background: 'transparent' }}>Secondary</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ACTIONS */}
      <div className="settings-actions">
        <button className="btn btn-danger" onClick={handleReset}>
          <RotateCcw size={16} /> Reset to Default
        </button>
        <p className="settings-note">Changes are saved automatically and will reflect across your dashboard and all generated documents.</p>
      </div>
    </div>
  );
};

export default SettingsPage;
